import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Search, History, Trash2, HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import FilterCheatSheet from "@/components/FilterCheatSheet";
import {
  describeFilters,
  FilterInterpretationError,
  interpretFilterQuery,
} from "@/lib/filterQuery";

export const UNMAPPABLE_QUERY_MESSAGE =
  "Couldn't map that to filters — try the filter builder or rephrase";

export interface SentenceFilterConfig<TFilterResult, TQueryResults> {
  entityName: string;
  /** Rebuilt on every call so date anchors are never frozen at import time. */
  buildSystemPrompt: () => string;
  /** Filter keys the API accepts; anything else the model emits is dropped. */
  allowedKeys: string[];
  /** Optional JSON schema used to force structured output server-side. */
  jsonSchema?: Record<string, unknown>;
  placeholder: string;
  cacheKey: string;
  fetchResults: (organizationId: string, filters: TFilterResult) => Promise<TQueryResults>;
}

interface CachedSearch<TFilterResult> {
  id: string;
  query: string;
  filters: TFilterResult;
  timestamp: number;
}

interface SentenceFilterProps<TFilterResult, TQueryResults> {
  config: SentenceFilterConfig<TFilterResult, TQueryResults>;
  onFiltersApplied: (
    filters: TFilterResult,
    results: TQueryResults,
    query: string
  ) => void;
}

export interface SentenceFilterRef {
  clearQuery: () => void;
}

const MAX_CACHED_SEARCHES = 10;

function SentenceFilter<TFilterResult, TQueryResults>(
  {
    config,
    onFiltersApplied,
  }: SentenceFilterProps<TFilterResult, TQueryResults>,
  ref: React.Ref<SentenceFilterRef>
) {
  const [query, setQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [cachedSearches, setCachedSearches] = useState<CachedSearch<TFilterResult>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState<string | null>(null);

  const { selectedOrganizationId } = useOrganizationsStore();

  // Load cached searches from localStorage
  useEffect(() => {
    const cached = localStorage.getItem(config.cacheKey);
    if (cached) {
      try {
        setCachedSearches(JSON.parse(cached));
      } catch (e) {
        console.error("Failed to parse cached searches:", e);
      }
    }
  }, [config.cacheKey]);

  // Expose clear function to parent
  useImperativeHandle(ref, () => ({
    clearQuery: () => {
      setQuery("");
    }
  }));

  // Save cached searches to localStorage
  const saveCachedSearches = (searches: CachedSearch<TFilterResult>[]) => {
    localStorage.setItem(config.cacheKey, JSON.stringify(searches));
    setCachedSearches(searches);
  };

  // Add a new search to cache
  const addToCache = (query: string, filters: TFilterResult) => {
    const newSearch: CachedSearch<TFilterResult> = {
      id: Date.now().toString(),
      query,
      filters,
      timestamp: Date.now(),
    };

    const updatedSearches = [newSearch, ...cachedSearches].slice(
      0,
      MAX_CACHED_SEARCHES
    );

    saveCachedSearches(updatedSearches);
  };

  // Remove a search from cache
  const removeFromCache = (id: string) => {
    const updatedSearches = cachedSearches.filter((search) => search.id !== id);
    saveCachedSearches(updatedSearches);
  };

  // Clear all cached searches
  const clearCache = () => {
    saveCachedSearches([]);
  };

  // Process natural language query using LLM
  const processQuery = async (queryText: string) => {
    if (!queryText.trim()) return;
    if (!selectedOrganizationId) {
      setError("No organization selected");
      return;
    }

    setIsProcessing(true);
    setError(null);

    setInterpretation(null);

    try {
      const { filters, filterCount } = await interpretFilterQuery<TFilterResult>(
        config,
        queryText
      );

      if (filterCount === 0) {
        setError(UNMAPPABLE_QUERY_MESSAGE);
        return;
      }

      setInterpretation(describeFilters(filters));
      addToCache(queryText, filters);
      await fetchResults(filters, queryText);
    } catch (error) {
      if (error instanceof FilterInterpretationError) {
        setError(error.message);
      } else {
        console.error("Failed to process query:", error);
        setError("Failed to process query. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Fetch results using the configured fetch function
  const fetchResults = async (filters: TFilterResult, queryText?: string) => {
    if (!selectedOrganizationId) return;

    setIsLoadingResults(true);
    try {
      const results = await config.fetchResults(selectedOrganizationId, filters);
      onFiltersApplied(filters, results, queryText || "");
    } catch (error) {
      console.error("Failed to fetch results:", error);
      setError(`Failed to fetch ${config.entityName} results. Please try again.`);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Apply a cached search
  const applyCachedSearch = async (cachedSearch: CachedSearch<TFilterResult>) => {
    setQuery(cachedSearch.query);
    setInterpretation(describeFilters(cachedSearch.filters));
    await fetchResults(cachedSearch.filters, cachedSearch.query);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processQuery(query);
  };

  return (
    <div className="space-y-4 mb-2">
      <Card>
        <CardHeader className="px-6 py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="h-4 w-4" />
              Smart {config.entityName} Search
            </CardTitle>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto max-w-4xl" align="end">
                  <FilterCheatSheet type={config.entityName.toLowerCase() as "patient" | "call"} />
                </PopoverContent>
              </Popover>
              {cachedSearches.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <History className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Recent Searches</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearCache}
                          className="h-7 px-2 text-muted-foreground hover:text-foreground"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {cachedSearches.slice(0, 5).map((search) => (
                          <div
                            key={search.id}
                            className="flex items-center justify-between p-2 bg-muted/50 rounded-md hover:bg-muted cursor-pointer"
                            onClick={() => applyCachedSearch(search)}
                          >
                            <span className="text-sm text-foreground truncate flex-1 pr-2">
                              {search.query}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {new Date(search.timestamp).toLocaleDateString()}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromCache(search.id);
                                }}
                                className="h-6 w-6 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Textarea
                placeholder={config.placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (query.trim() && !isProcessing && !isLoadingResults) {
                      processQuery(query);
                    }
                  }
                }}
                className="min-h-[60px]"
                disabled={isProcessing || isLoadingResults}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                variant="outline"
                disabled={!query.trim() || isProcessing || isLoadingResults}
                className="flex-1"
              >
                {isProcessing ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Processing Query...
                  </>
                ) : isLoadingResults ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Loading Results...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Search {config.entityName}
                  </>
                )}
              </Button>
            </div>
          </form>

          {interpretation && !error && (
            <div className="text-xs text-muted-foreground break-words">
              Interpreted as: {interpretation}
            </div>
          )}

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default forwardRef(SentenceFilter) as <TFilterResult, TQueryResults>(
  props: SentenceFilterProps<TFilterResult, TQueryResults> & {
    ref?: React.Ref<SentenceFilterRef>;
  }
) => ReturnType<typeof SentenceFilter>;